import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractTemplate } from '../../../hooks/useContractTemplate';
import Card from '../../../components/Card/Card';
import ListContainer from '../../../components/ListContainer/ListContainer';
import ListElement from '../../../components/ListElement/ListElement';
import ElementActions from '../../../components/ElementActions/ElementActions';
import Subtitle from '../../../components/Subtitle/Subtitle';
import TinyText from '../../../components/TinyText/TinyText';
import SecondaryButton from '../../../components/SecondaryButton/SecondaryButton';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import TextArea from '../../../components/TextArea/TextArea';
import IconButton from '../../../components/IconButton/IconButton';
import { Icon } from '../../../components/Icon/Icon';
import deleteIcon from '../../../assets/images/bin.svg';
import { isValidText } from '../../../shared/validation';

const DEFAULT_FINAL_ARRANGEMENTS_KEYS = [
  'contracts.final_arrangements.point1',
  'contracts.final_arrangements.point2',
  'contracts.final_arrangements.point3',
  'contracts.final_arrangements.point4',
  'contracts.final_arrangements.point5',
  'contracts.final_arrangements.point6',
  'contracts.final_arrangements.point7',
  'contracts.final_arrangements.point8',
  'contracts.final_arrangements.point9',
  'contracts.final_arrangements.point10',
];

type RemarkItem = { id: string; text: string };

type Props = {
  contractTemplate: ContractTemplate;
  updateContractTemplate: (value: any) => void;
};

export default function RemarksSection({ contractTemplate, updateContractTemplate }: Props) {
  const { t } = useTranslation();
  const currentRemarks = contractTemplate?.remarks?.conditions || [];
  const [newItem, setNewItem] = useState('');

  const elements: RemarkItem[] = currentRemarks.map((text, i) => ({
    id: `remark-${i}`,
    text,
  }));

  const handleAddDefaultConditions = () => {
    const defaultTexts = DEFAULT_FINAL_ARRANGEMENTS_KEYS.map((key) => t(key)).filter(Boolean);
    updateContractTemplate({ remarks: { conditions: [...currentRemarks, ...defaultTexts] } });
  };

  const handleMovedItem = (reordered: RemarkItem[]) => {
    updateContractTemplate({ remarks: { conditions: reordered.map((item) => item.text) } });
  };

  const handleDeleteItem = (index: number) => {
    const updated = currentRemarks.filter((_, i) => i !== index);
    updateContractTemplate({ remarks: { conditions: updated } });
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      updateContractTemplate({ remarks: { conditions: [...currentRemarks, newItem.trim()] } });
      setNewItem('');
    }
  };

  return (
    <Card>
      <>
        <Subtitle text={t('contract_template.remarks.title')} />
        <TinyText text={t('contract_template.remarks.hint')} />
        <div className="mt-2 mb-3">
          <SecondaryButton
            text={t('contract_template.remarks.add_default_conditions') ?? 'Add default conditions'}
            fn={handleAddDefaultConditions}
          />
        </div>
        <ListContainer
          elements={elements}
          enableDragAndDrop
          onMovedItem={handleMovedItem}
          toElementFn={(item: RemarkItem, index: number) => (
            <ListElement>
              <ElementActions
                actions={
                  <IconButton onClick={() => handleDeleteItem(index)}>
                    <Icon url={deleteIcon} cssStyles="w-5" />
                  </IconButton>
                }
              >
                <div className="flex-1 min-w-0">{item.text}</div>
              </ElementActions>
            </ListElement>
          )}
        />
        <TextArea
          label={String(t('contract_template.remarks.form.new_condition.label') ?? '')}
          validationFn={(value) => !!value && isValidText(value, 5)}
          defaultValue={newItem}
          onChange={(e) => setNewItem(e.target.value)}
        />
        <div className="container flex flex-wrap space-x-5 mt-2">
          <PrimaryButton
            text={String(t('contract_template.remarks.form.button') ?? '')}
            fn={handleAddItem}
          />
        </div>
      </>
    </Card>
  );
}
